import { useMemo } from 'react';
import {
    EEGTariff,
    MarktpreisTariff,
    PPATariff,
    Ertragsprognose,
    AktiverErtragswert,
    EnergyRevenuesResponse,
    EnergyRevenueEntry,
    PPARevenueDetail,
} from '../../../../types';
import { getErtragswertFromPrognose } from './useTariffsData';

interface UseComputedRevenuesParams {
    /** EEG-Tarif mit berechneten Feldern (Zuschlagswert, Korrekturfaktor) */
    eegTariff: EEGTariff | null;
    /** Alle PPA-Tarife des Projekts */
    ppaTariffs: PPATariff[];
    /** Marktpreis-Tarif (Default: 6 Cent/kWh ab Jahr 21) */
    marktpreisTariff: MarktpreisTariff | null;
    /** Aktive Ertragsprognose für den Jahresertrag */
    activeYieldForecast: Ertragsprognose | null;
    /** Aktiver Ertragswert-Typ (Bruttoertrag, P50, P75, P90) */
    aktiverErtragswert: AktiverErtragswert | undefined;
    /** Projektlaufzeit in Jahren */
    laufzeitJahre: number;
    /** Projekt-ID für die Response */
    projectId: string;
}

/**
 * Berechnet die Energieerlöse pro Jahr im Frontend (Client-Side Derived State).
 * 
 * Duale Berechnung: Entspricht calculate_energy_revenues im Backend
 * (domain/tariffs/service.py → EnergyRevenueService.calculate_and_save).
 * Shared Fixtures in backend_python/tests/fixtures/shared_calculation_fixtures.json
 * stellen die Konsistenz sicher.
 * 
 * DAG Edge: Aktiver_Ertragswert, EEG_Preis_kWh, Aktive_PPAs, Marktpreis, Kommunale_Beteiligung, Einkaufspreis → Energieerlöse_pro_Jahr
 * 
 * Berechnungslogik:
 * 1. PPA-Erlöse = volle vertragliche Menge × PPA-Preis (Fehlmenge wird separat zugekauft)
 * 2. Verbleibende kWh (Eigenerzeugung − gelieferte PPA-Menge): EEG (Jahr 1-20) oder Marktpreis (ab Jahr 21)
 * 
 * Vorteile gegenüber Backend-Fetch:
 * - Keine Race Condition (Chart ist immer konsistent mit den angezeigten Tarifen)
 * - Sofortiges UI-Update bei Tarifänderungen
 * - Kein setTimeout-Hack für Backend-Background-Tasks
 */
export const useComputedRevenues = ({
    eegTariff,
    ppaTariffs,
    marktpreisTariff,
    activeYieldForecast,
    aktiverErtragswert,
    laufzeitJahre,
    projectId,
}: UseComputedRevenuesParams): EnergyRevenuesResponse | null => {
    return useMemo(() => {
        // Ertragswert aus der aktiven Prognose ermitteln (in MWh)
        const ertragswertMwh = getErtragswertFromPrognose(activeYieldForecast, aktiverErtragswert);
        if (!ertragswertMwh || ertragswertMwh <= 0) return null;

        // Jahresertrag in kWh umrechnen
        const jahresertragKwh = ertragswertMwh * 1000;

        // EEG-Preis berechnen: Zuschlagswert × Korrekturfaktor (in Cent/kWh)
        const eegPreisCent =
            eegTariff?.zuschlagswert != null && eegTariff?.korrekturfaktor != null
                ? eegTariff.zuschlagswert * eegTariff.korrekturfaktor
                : null;

        // Marktpreis (Default: 6 Cent/kWh)
        const marktpreisCent = marktpreisTariff?.marktpreis_kwh ?? 6.0;
        const kommunalCent = marktpreisTariff?.kommunale_beteiligung_kwh ?? 0.2;
        const einkaufspreisCent = marktpreisTariff?.einkaufspreis_kwh ?? 10.0;

        // PPAs sortiert nach created_at (älteste zuerst = höchste Priorität)
        const sortedPPAs = [...ppaTariffs].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

        // Erlöse für jedes Jahr berechnen
        const eintraege: EnergyRevenueEntry[] = [];
        let summeGesamt = 0;

        for (let jahr = 1; jahr <= laufzeitJahre; jahr++) {
            let verfuegbarKwh = jahresertragKwh;

            // 1. PPA-Erlöse berechnen (Priorität)
            let erloesPpa = 0;
            const ppaDetails: PPARevenueDetail[] = [];

            // Aktive PPAs für dieses Jahr filtern
            const aktivePPAs = sortedPPAs.filter(
                ppa => ppa.beginn_jahr <= jahr && jahr <= ppa.ende_jahr
            );

            for (const ppa of aktivePPAs) {
                // Voller PPA-Erlös = vertragliche Menge × Preis (Fehlmenge wird separat zugekauft)
                const ppaErloes = ppa.abnahmemenge_kwh_jahr * ppa.preis_kwh / 100;
                erloesPpa += ppaErloes;

                // Eigenerzeugung für diesen PPA reduzieren (gedeckelt auf verfügbare Menge)
                const geliefertKwh = Math.min(ppa.abnahmemenge_kwh_jahr, verfuegbarKwh);
                verfuegbarKwh -= geliefertKwh;

                ppaDetails.push({
                    ppa_id: ppa.id,
                    bezeichnung: ppa.bezeichnung,
                    erloes_eur: ppaErloes,
                    menge_kwh: ppa.abnahmemenge_kwh_jahr,
                    preis_cent: ppa.preis_kwh,
                });
            }

            // 2. Verbleibende kWh: EEG (Jahr 1-20) oder Marktpreis (ab Jahr 21)
            let erloesEeg = 0;
            let erloesMarktpreis = 0;

            if (verfuegbarKwh > 0) {
                if (jahr <= 20) {
                    // EEG-Tarif (nur wenn berechenbar)
                    if (eegPreisCent != null) {
                        erloesEeg = verfuegbarKwh * eegPreisCent / 100;
                    }
                } else {
                    // Marktpreis ab Jahr 21
                    erloesMarktpreis = verfuegbarKwh * marktpreisCent / 100;
                }
            }

            // Gesamterlös (Brutto)
            const erloesEur = erloesEeg + erloesPpa + erloesMarktpreis;

            // Kommunale Beteiligung: Satz × gesamter Jahresertrag
            const abzugKommunal = jahresertragKwh * kommunalCent / 100;

            // PPA-Zukauf: Fehlmenge × Einkaufspreis
            let zukaufPpa = 0;
            if (einkaufspreisCent != null && aktivePPAs.length > 0) {
                const gesamtPpaNachfrage = aktivePPAs.reduce((s, p) => s + p.abnahmemenge_kwh_jahr, 0);
                const fehlmenge = Math.max(0, gesamtPpaNachfrage - jahresertragKwh);
                zukaufPpa = fehlmenge * einkaufspreisCent / 100;
            }

            const nettoErloesEur = erloesEur - abzugKommunal - zukaufPpa;

            eintraege.push({
                jahr,
                erloes_eeg: erloesEeg,
                erloes_ppa: erloesPpa,
                erloes_marktpreis: erloesMarktpreis,
                ppa_details: ppaDetails,
                erloes_eur: erloesEur,
                abzug_kommunal: abzugKommunal,
                zukauf_ppa: zukaufPpa,
                netto_erloes_eur: nettoErloesEur,
            });

            summeGesamt += erloesEur;
        }

        // Wenn keine Erlöse berechnet werden konnten (z.B. kein EEG und keine PPAs), null zurückgeben
        if (eintraege.length === 0) return null;

        return {
            project_id: projectId,
            eintraege,
            summe_gesamt: summeGesamt,
            aktiver_ertragswert_typ: aktiverErtragswert,
            ertragswert_mwh: ertragswertMwh,
            zuschlagswert_cent: eegTariff?.zuschlagswert ?? undefined,
        };
    }, [
        eegTariff?.zuschlagswert,
        eegTariff?.korrekturfaktor,
        ppaTariffs,
        marktpreisTariff?.marktpreis_kwh,
        marktpreisTariff?.kommunale_beteiligung_kwh,
        marktpreisTariff?.einkaufspreis_kwh,
        activeYieldForecast,
        aktiverErtragswert,
        laufzeitJahre,
        projectId,
    ]);
};
